import React from 'react'
import {
  SecondaryMarketOrder,
  SecondaryMarketPortalData,
} from '../../services/secondary-market-service'
import './secondary-market.scss'

type SecondaryMarketPresenterProps = {
  portal: SecondaryMarketPortalData | null
  loading: boolean
  activeTab: 'browse' | 'requests' | 'trades'
  sellModalOpen: boolean
  selectedOpportunityId: number | null
  sellQuantity: string
  priceBasis: 'AT_REFERENCE' | 'LIMIT'
  limitPrice: string
  submitting: boolean
  buyModalOpen: boolean
  buyListing: SecondaryMarketOrder | null
  offerPrice: string
  onTabChange: (tab: 'browse' | 'requests' | 'trades') => void
  onOpenSellModal: (opportunityId?: number) => void
  onCloseSellModal: () => void
  onSelectedOpportunityChange: (id: number | null) => void
  onSellQuantityChange: (value: string) => void
  onPriceBasisChange: (value: 'AT_REFERENCE' | 'LIMIT') => void
  onLimitPriceChange: (value: string) => void
  onSellSubmit: () => void
  onOpenBuyModal: (listing: SecondaryMarketOrder) => void
  onCloseBuyModal: () => void
  onOfferPriceChange: (value: string) => void
  onBuyInterestSubmit: () => void
  onCancelOrder: (order: SecondaryMarketOrder) => void
  onContactRm: (trade?: SecondaryMarketOrder) => void
}

const formatPrice = (value: number | null | undefined) =>
  value !== null && Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '—'

const askingBasis = (priceBasis: string, limitPrice: number | null) => {
  if (priceBasis === 'LIMIT' && limitPrice !== null) {
    return `Limit @ ${formatPrice(limitPrice)}`
  }
  return 'At reference'
}

/** Seller asking price, or buyer offer price for buy-interest rows. */
const orderPriceLabel = (order: {
  type: string
  price_basis: string
  limit_price: number | null
  reference_price: number | null
}) => {
  if (order.type === 'BUY_INTEREST') {
    return order.limit_price !== null ? formatPrice(order.limit_price) : '—'
  }
  if (order.price_basis === 'LIMIT' && order.limit_price !== null) {
    return formatPrice(order.limit_price)
  }
  return formatPrice(order.reference_price)
}

const tabLabel = (tab: 'browse' | 'requests' | 'trades') => {
  if (tab === 'browse') return 'Browse'
  if (tab === 'requests') return 'My requests'
  return 'Trades'
}

export const SecondaryMarketPresenter: React.FC<SecondaryMarketPresenterProps> = ({
  portal,
  loading,
  activeTab,
  sellModalOpen,
  selectedOpportunityId,
  sellQuantity,
  priceBasis,
  limitPrice,
  submitting,
  buyModalOpen,
  buyListing,
  offerPrice,
  onTabChange,
  onOpenSellModal,
  onCloseSellModal,
  onSelectedOpportunityChange,
  onSellQuantityChange,
  onPriceBasisChange,
  onLimitPriceChange,
  onSellSubmit,
  onOpenBuyModal,
  onCloseBuyModal,
  onOfferPriceChange,
  onBuyInterestSubmit,
  onCancelOrder,
  onContactRm,
}) => {
  const settings = portal?.settings
  const windowOpen = settings?.is_open ?? false
  const listings = portal?.listings ?? []
  const myOrders = portal?.myOrders ?? []
  const trades = portal?.trades ?? []
  const eligibleHoldings = portal?.eligibleHoldings ?? []

  return (
    <div className='secondary-market-portal'>
      <header className='secondary-market-portal__header'>
        <div className='secondary-market-portal__header-text'>
          <h1>Secondary Market</h1>
          <p
            className={`secondary-market-portal__window-status${
              windowOpen ? ' secondary-market-portal__window-status--open' : ''
            }`}
          >
            {windowOpen
              ? `Trading window open${settings?.window_label ? `: ${settings.window_label}` : ''}`
              : 'Trading window is currently closed'}
          </p>
        </div>
        <div className='secondary-market-portal__header-actions'>
          <button
            type='button'
            className='secondary-market-portal__btn secondary-market-portal__btn--ghost'
            onClick={() => onContactRm()}
          >
            Contact RM
          </button>
          {windowOpen ? (
            <button
              type='button'
              className='secondary-market-portal__btn'
              onClick={() => onOpenSellModal()}
            >
              List holdings for sale
            </button>
          ) : null}
        </div>
      </header>

      <nav className='secondary-market-portal__tabs' aria-label='Secondary market sections'>
        {(['browse', 'requests', 'trades'] as const).map((tab) => (
          <button
            key={tab}
            type='button'
            className={`secondary-market-portal__tab${activeTab === tab ? ' active' : ''}`}
            onClick={() => onTabChange(tab)}
          >
            {tabLabel(tab)}
          </button>
        ))}
      </nav>

      {loading ? <p className='secondary-market-portal__loading'>Loading…</p> : null}

      {!loading && activeTab === 'browse' ? (
        <section className='secondary-market-portal__panel'>
          {!windowOpen ? (
            <p className='secondary-market-portal__empty'>
              Listings are visible when the trading window is open.
            </p>
          ) : listings.length === 0 ? (
            <p className='secondary-market-portal__empty'>
              No sell listings are available in this window yet.
            </p>
          ) : (
            <div className='secondary-market-portal__table-wrap'>
              <table className='secondary-market-portal__table'>
                <thead>
                  <tr>
                    <th>Opportunity</th>
                    <th>Quantity</th>
                    <th>Reference price</th>
                    <th>Asking price</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {listings.map((listing) => {
                    const alreadyExpressed = myOrders.some(
                      (o) =>
                        o.type === 'BUY_INTEREST' &&
                        o.status === 'OPEN' &&
                        Number(o.metadata?.target_sell_order_id) === listing.id,
                    )
                    return (
                      <tr key={listing.id}>
                        <td>{listing.opportunity_title ?? listing.opportunity_id}</td>
                        <td>{listing.quantity}</td>
                        <td>{formatPrice(listing.reference_price)}</td>
                        <td>{askingBasis(listing.price_basis, listing.limit_price)}</td>
                        <td>
                          <button
                            type='button'
                            className='secondary-market-portal__btn secondary-market-portal__btn--sm'
                            disabled={alreadyExpressed}
                            onClick={() => onOpenBuyModal(listing)}
                          >
                            {alreadyExpressed ? 'Interest submitted' : 'Express interest'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {!loading && activeTab === 'requests' ? (
        <section className='secondary-market-portal__panel'>
          {myOrders.length === 0 ? (
            <p className='secondary-market-portal__empty'>
              You have not submitted any buy or sell requests yet.
            </p>
          ) : (
            <div className='secondary-market-portal__table-wrap'>
              <table className='secondary-market-portal__table'>
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Type</th>
                    <th>Opportunity</th>
                    <th>Qty</th>
                    <th>Asking / Offer</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {myOrders.map((order) => (
                    <tr key={order.id}>
                      <td>{order.order_id}</td>
                      <td>{order.type === 'BUY_INTEREST' ? 'Buy interest' : 'Sell'}</td>
                      <td>{order.opportunity_title ?? order.opportunity_id}</td>
                      <td>{order.quantity}</td>
                      <td>
                        {order.type === 'BUY_INTEREST'
                          ? `Offer @ ${orderPriceLabel(order)}`
                          : order.price_basis === 'LIMIT'
                          ? `Ask @ ${orderPriceLabel(order)}`
                          : `At ref ${orderPriceLabel(order)}`}
                      </td>
                      <td>
                        <span
                          className={`secondary-market-portal__status secondary-market-portal__status--${order.status.toLowerCase()}`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td>
                        {order.status === 'OPEN' ? (
                          <button
                            type='button'
                            className='secondary-market-portal__btn secondary-market-portal__btn--ghost secondary-market-portal__btn--sm'
                            onClick={() => onCancelOrder(order)}
                          >
                            {order.type === 'BUY_INTEREST'
                              ? 'Withdraw interest'
                              : 'Withdraw listing'}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {!loading && activeTab === 'trades' ? (
        <section className='secondary-market-portal__panel'>
          {trades.length === 0 ? (
            <p className='secondary-market-portal__empty'>
              Matched and settled trades will appear here.
            </p>
          ) : (
            <div className='secondary-market-portal__table-wrap'>
              <table className='secondary-market-portal__table'>
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Opportunity</th>
                    <th>Agreed price</th>
                    <th>Qty</th>
                    <th>Status</th>
                    <th>Settlement</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {trades.map((trade) => (
                    <tr key={trade.id}>
                      <td>{trade.order_id}</td>
                      <td>{trade.opportunity_title ?? trade.opportunity_id}</td>
                      <td>{formatPrice(trade.agreed_unit_price)}</td>
                      <td>{trade.quantity}</td>
                      <td>
                        <span
                          className={`secondary-market-portal__status secondary-market-portal__status--${trade.status.toLowerCase()}`}
                        >
                          {trade.status}
                        </span>
                      </td>
                      <td>{trade.settlement_status ?? '—'}</td>
                      <td>
                        <button
                          type='button'
                          className='secondary-market-portal__btn secondary-market-portal__btn--ghost secondary-market-portal__btn--sm'
                          onClick={() => onContactRm(trade)}
                        >
                          Contact RM
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {sellModalOpen ? (
        <div
          className='secondary-market-portal__modal-backdrop'
          role='presentation'
          onClick={(e) => {
            if (e.target === e.currentTarget) onCloseSellModal()
          }}
        >
          <div
            className='secondary-market-portal__modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='sm-sell-title'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='secondary-market-portal__modal-header'>
              <div>
                <h2 id='sm-sell-title'>List holdings for sale</h2>
                <p className='secondary-market-portal__modal-subtitle'>
                  Choose an eligible holding, quantity, and price basis for this trading window.
                </p>
              </div>
              <button
                type='button'
                className='secondary-market-portal__modal-close'
                aria-label='Close dialog'
                onClick={onCloseSellModal}
              >
                ×
              </button>
            </div>

            <div className='secondary-market-portal__modal-form'>
              <div className='secondary-market-portal__field'>
                <label htmlFor='sm-holding'>Holding</label>
                <select
                  id='sm-holding'
                  value={selectedOpportunityId ?? ''}
                  onChange={(e) =>
                    onSelectedOpportunityChange(e.target.value ? Number(e.target.value) : null)
                  }
                >
                  <option value=''>Select holding</option>
                  {eligibleHoldings.map((h) => (
                    <option key={h.opportunity_id} value={h.opportunity_id}>
                      {h.opportunity_title} (available: {h.available_quantity})
                    </option>
                  ))}
                </select>
              </div>

              <div className='secondary-market-portal__field'>
                <label htmlFor='sm-quantity'>Quantity</label>
                <input
                  id='sm-quantity'
                  type='number'
                  step='0.000001'
                  min='0'
                  value={sellQuantity}
                  onChange={(e) => onSellQuantityChange(e.target.value)}
                  placeholder='Enter quantity to sell'
                />
              </div>

              <div className='secondary-market-portal__field'>
                <label htmlFor='sm-price-basis'>Price basis</label>
                <select
                  id='sm-price-basis'
                  value={priceBasis}
                  onChange={(e) => onPriceBasisChange(e.target.value as 'AT_REFERENCE' | 'LIMIT')}
                >
                  <option value='AT_REFERENCE'>At reference</option>
                  <option value='LIMIT'>Limit</option>
                </select>
              </div>

              {priceBasis === 'LIMIT' ? (
                <div className='secondary-market-portal__field'>
                  <label htmlFor='sm-limit-price'>Limit price</label>
                  <input
                    id='sm-limit-price'
                    type='number'
                    step='0.01'
                    min='0'
                    value={limitPrice}
                    onChange={(e) => onLimitPriceChange(e.target.value)}
                    placeholder='Enter limit unit price'
                  />
                </div>
              ) : null}
            </div>

            <div className='secondary-market-portal__modal-footer'>
              <button
                type='button'
                className='secondary-market-portal__btn secondary-market-portal__btn--secondary'
                onClick={onCloseSellModal}
              >
                Cancel
              </button>
              <button
                type='button'
                className='secondary-market-portal__btn'
                disabled={submitting || !selectedOpportunityId || !sellQuantity}
                onClick={onSellSubmit}
              >
                {submitting ? 'Submitting…' : 'Submit sell request'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {buyModalOpen && buyListing ? (
        <div
          className='secondary-market-portal__modal-backdrop'
          role='presentation'
          onClick={(e) => {
            if (e.target === e.currentTarget) onCloseBuyModal()
          }}
        >
          <div
            className='secondary-market-portal__modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='sm-buy-title'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='secondary-market-portal__modal-header'>
              <div>
                <h2 id='sm-buy-title'>Express buy interest</h2>
                <p className='secondary-market-portal__modal-subtitle'>
                  {buyListing.opportunity_title ?? buyListing.opportunity_id} · qty{' '}
                  {buyListing.quantity} · seller asking{' '}
                  {askingBasis(buyListing.price_basis, buyListing.limit_price)}
                </p>
              </div>
              <button
                type='button'
                className='secondary-market-portal__modal-close'
                aria-label='Close dialog'
                onClick={onCloseBuyModal}
              >
                ×
              </button>
            </div>

            <div className='secondary-market-portal__modal-form'>
              <div className='secondary-market-portal__field'>
                <label htmlFor='sm-offer-price'>Your offer price (per unit)</label>
                <input
                  id='sm-offer-price'
                  type='number'
                  step='0.01'
                  min='0'
                  value={offerPrice}
                  onChange={(e) => onOfferPriceChange(e.target.value)}
                  placeholder='Enter your offer unit price'
                />
              </div>
            </div>

            <div className='secondary-market-portal__modal-footer'>
              <button
                type='button'
                className='secondary-market-portal__btn secondary-market-portal__btn--secondary'
                onClick={onCloseBuyModal}
              >
                Cancel
              </button>
              <button
                type='button'
                className='secondary-market-portal__btn'
                disabled={submitting || !offerPrice || Number(offerPrice) <= 0}
                onClick={onBuyInterestSubmit}
              >
                {submitting ? 'Submitting…' : 'Submit buy interest'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
